import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useAuthenticatedMediaUrl } from '@/hooks/useAuthenticatedMedia';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  imgClassName?: string;
  imgStyle?: React.CSSProperties;
  thumbnailSrc?: string;
  placeholderColor?: string;
  aspectRatio?: 'square' | 'video' | 'portrait' | 'auto';
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Lazy loading image component with placeholder support
 * Uses Intersection Observer to only load images when they enter the viewport
 */
export function LazyImage({
  src,
  alt,
  className,
  style,
  imgClassName,
  imgStyle,
  thumbnailSrc,
  placeholderColor = 'bg-muted',
  aspectRatio = 'auto',
  onLoad,
  onError,
}: LazyImageProps) {
  const resolvedSrc = useAuthenticatedMediaUrl(src) ?? src;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const aspectClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    portrait: 'aspect-[3/4]',
    auto: '',
  };

  return (
    <div
      ref={imgRef}
      className={cn(
        'relative overflow-hidden',
        aspectClasses[aspectRatio],
        placeholderColor,
        className
      )}
      style={style}
    >
      {/* Thumbnail/placeholder (low quality, loads immediately) */}
      {thumbnailSrc && !isLoaded && (
        <img
          src={thumbnailSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-sm scale-105"
          aria-hidden="true"
        />
      )}

      {/* Main image (loads when in view) */}
      {isInView && !hasError && (
        <img
          src={resolvedSrc}
          alt={alt}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0',
            imgClassName
          )}
          style={imgStyle}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
        />
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <span className="text-muted-foreground text-sm">Failed to load</span>
        </div>
      )}

      {/* Loading shimmer */}
      {!isLoaded && !hasError && !thumbnailSrc && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-muted via-muted/50 to-muted" />
      )}
    </div>
  );
}

/**
 * Simple image component with built-in lazy loading
 * For cases where you don't need thumbnail support
 */
export function SimpleImage({
  src,
  alt,
  className,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className={cn('relative overflow-hidden bg-muted', className)}>
      <img
        src={src}
        alt={alt}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
        onLoad={() => setIsLoaded(true)}
        loading="lazy"
        {...props}
      />
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
    </div>
  );
}

export default LazyImage;
